<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import TimezoneSelector from '@/components/TimezoneSelector.vue'
import axios from '@/axios.js'
import { useUserStore } from '@/stores/userStore'
import BoxscoresManagementSettings from './settings/BoxscoresManagementSettings.vue'
import ProjectionModelBackendToggle from '@/components/ProjectionModelBackendToggle.vue'
import ApiTokenManager from '@/components/ApiTokenManager.vue'
import WebhookClientsSettings from './settings/WebhookClientsSettings.vue'
import UiTemplatePicker from '@/components/UiTemplatePicker.vue'

const userStore = useUserStore()
const route = useRoute()
const activeTab = ref(route.query.tab || 'general')

// Filter tabs based on user role - only admins see general, appearance, notifications, security, integrations, boxscores-management
const availableTabs = computed(() => {
  const isAdmin = userStore.currentUser?.role === 'admin'
  if (!isAdmin) {
    return ['team', 'api-keys']
  }
  return ['general', 'appearance', 'notifications', 'security', 'team', 'api-keys', 'integrations', 'webhook-clients', 'boxscores-management']
})

const tabLabels = {
  general: 'General',
  appearance: 'Appearance',
  notifications: 'Notifications',
  security: 'Security',
  team: 'Team',
  'api-keys': 'API Keys',
  integrations: 'Integrations',
  'webhook-clients': 'Webhook clients',
  'boxscores-management': 'Boxscores Management'
}

// Ensure activeTab is valid for current user (e.g. non-admin has no 'general')
watch(availableTabs, (tabs) => {
  if (tabs.includes(route.query.tab)) {
    activeTab.value = route.query.tab
  } else if (tabs.length && !tabs.includes(activeTab.value)) {
    activeTab.value = tabs[0]
  }
}, { immediate: true })

// Support ?tab=team query param to open specific tab
watch(() => route.query.tab, (tab) => {
  if (tab && availableTabs.value.includes(tab)) {
    activeTab.value = tab
  }
}, { immediate: true })

// Password change functionality
const showPasswordChangeModal = ref(false)
const passwordChangeForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})
const changingPassword = ref(false)
const passwordChangeError = ref('')
const passwordChangeSuccess = ref(false)

const generalSettings = ref({
  siteName: 'OddsComposer',
  timezone: 'UTC',
  dateFormat: 'DD.MM.YYYY',
  timeFormat: '24h',
  defaultLanguage: 'en',
  maintenanceMode: false
})

const savingTimezone = ref(false)
const showTimezoneSuccessPopup = ref(false)
const updatedTimezone = ref('')

const notificationSettings = ref({
  emailNotifications: true,
  pushNotifications: false,
  notifyOnGameStart: true,
  notifyOnStatusChange: true,
  dailyReport: true,
  weeklyReport: true
})

const securitySettings = ref({
  twoFactorAuth: true,
  sessionTimeout: 30,
  passwordExpiry: 90,
  ipWhitelist: '',
  maxLoginAttempts: 5,
  requireStrongPassword: true
})

const integrationSettings = ref({
  apiKey: 'ak_live_****1234',
  webhookUrl: '',
  callbackUrl: '',
  apiRateLimit: 1000,
  enableWebhooks: false,
  enableCallbacks: false
})

const saveSettings = () => {
  // Implement settings save logic
  console.log('Settings saved')
}

// Password change functions
const openPasswordChangeModal = () => {
  passwordChangeForm.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  }
  passwordChangeError.value = ''
  passwordChangeSuccess.value = false
  showPasswordChangeModal.value = true
}

const closePasswordChangeModal = () => {
  showPasswordChangeModal.value = false
  passwordChangeForm.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  }
  passwordChangeError.value = ''
  passwordChangeSuccess.value = false
}

const validatePasswordForm = () => {
  const { currentPassword, newPassword, confirmPassword } = passwordChangeForm.value
  
  if (!currentPassword.trim()) {
    passwordChangeError.value = 'Current password is required'
    return false
  }
  
  if (!newPassword.trim()) {
    passwordChangeError.value = 'New password is required'
    return false
  }
  
  if (newPassword.length < 8) {
    passwordChangeError.value = 'New password must be at least 8 characters long'
    return false
  }
  
  if (newPassword !== confirmPassword) {
    passwordChangeError.value = 'New passwords do not match'
    return false
  }
  
  if (newPassword === currentPassword) {
    passwordChangeError.value = 'New password must be different from current password'
    return false
  }
  
  passwordChangeError.value = ''
  return true
}

const updateUserPassword = async () => {
  if (!validatePasswordForm()) return
  
  changingPassword.value = true
  passwordChangeError.value = ''
  
  try {
    // Get current user ID from localStorage or user store
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
    const userId = currentUser.id || 1 // Fallback to 1 if not found
    
    // Update user password via the dedicated password endpoint
    const response = await axios.put(`/api/users/${userId}/password`, {
      current_password: passwordChangeForm.value.currentPassword,
      password: passwordChangeForm.value.newPassword,
      password_confirmation: passwordChangeForm.value.confirmPassword
    })
    
    if (response.data) {
      passwordChangeSuccess.value = true
      passwordChangeForm.value = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      
      // Show success message for a few seconds then close
      setTimeout(() => {
        closePasswordChangeModal()
      }, 2000)
      
      console.log('Password updated successfully')
    }
  } catch (error) {
    console.error('Failed to update password:', error)
    passwordChangeError.value = error.response?.data?.message || 'Failed to update password. Please try again.'
  } finally {
    changingPassword.value = false
  }
}

// Team Management functionality
const teamMembers = ref([])
const loadingTeamMembers = ref(false)
const teamSearchQuery = ref('')
const showAddTeamMemberModal = ref(false)
const showTeamMemberDetailsModal = ref(false)
const showDeleteMemberModal = ref(false)
const memberToDelete = ref(null)
const deletingMember = ref(false)
const showDeleteSuccessPopup = ref(false)
const selectedTeamMember = ref(null)
const addingTeamMember = ref(false)

// Add team member form
const newTeamMemberForm = ref({
  name: '',
  email: '',
  role: 'client',
  password: '',
  password_confirmation: ''
})

// Get current user info
const currentUser = ref(null)
const apiKeysUser = computed(() => userStore.currentUser || currentUser.value)

// Computed property for filtered team members
const filteredTeamMembers = computed(() => {
  let members = teamMembers.value || []

  // Filter by search query
  if (teamSearchQuery.value) {
    const query = teamSearchQuery.value.toLowerCase()
    members = members.filter(member => 
      (member.name || '').toLowerCase().includes(query) ||
      (member.email || '').toLowerCase().includes(query)
    )
  }

  return members
})

// Fetch team members from the same organization
const fetchTeamMembers = async () => {
  loadingTeamMembers.value = true
  try {
    // Get current user info
    const currentUserData = JSON.parse(localStorage.getItem('currentUser') || '{}')
    currentUser.value = currentUserData
    
    // Fetch users from the same organization using team-members endpoint
    const response = await axios.get('/api/team-members', {
      params: {
        exclude_current: true // Exclude current user from team list
      }
    })
    
    teamMembers.value = response.data || []
  } catch (error) {
    console.error('Failed to fetch team members:', error)
    teamMembers.value = []
  } finally {
    loadingTeamMembers.value = false
  }
}

// Add new team member
const addTeamMember = async () => {
  if (!newTeamMemberForm.value.name || !newTeamMemberForm.value.email || !newTeamMemberForm.value.password) {
    alert('Please fill in all required fields')
    return
  }

  if (newTeamMemberForm.value.password !== newTeamMemberForm.value.password_confirmation) {
    alert('Passwords do not match')
    return
  }

  addingTeamMember.value = true
  
  try {
    // Use the team-members endpoint which automatically sets organization and organisation
    // based on the current logged-in user's organization
    const response = await axios.post('/api/team-members', {
      name: newTeamMemberForm.value.name,
      email: newTeamMemberForm.value.email,
      password: newTeamMemberForm.value.password,
      password_confirmation: newTeamMemberForm.value.password_confirmation,
      role: newTeamMemberForm.value.role,
      status: 'active'
    })
    
    if (response.data) {
      // Add to local team members list
      // Backend returns the user directly or nested in 'user' field
      const newMember = response.data.user || response.data
      if (newMember) {
        teamMembers.value.push(newMember)
      }
      
      // Reset form
      newTeamMemberForm.value = {
        name: '',
        email: '',
        role: 'client',
        password: '',
        password_confirmation: ''
      }
      
      // Close modal
      showAddTeamMemberModal.value = false
      
      console.log('Team member added successfully')
    }
  } catch (error) {
    console.error('Failed to add team member:', error)
    alert('Failed to add team member: ' + (error.response?.data?.message || error.message))
  } finally {
    addingTeamMember.value = false
  }
}

// View team member details
const viewTeamMemberDetails = (member) => {
  selectedTeamMember.value = member
  showTeamMemberDetailsModal.value = true
}

// Close team member details modal
const closeTeamMemberDetailsModal = () => {
  showTeamMemberDetailsModal.value = false
  selectedTeamMember.value = null
}

// Update team member
const updateTeamMember = async (memberId, updates) => {
  try {
    const response = await axios.put(`/api/users/${memberId}`, updates)
    
    if (response.data) {
      // Update local team member
      const memberIndex = teamMembers.value.findIndex(m => m.id === memberId)
      if (memberIndex !== -1) {
        teamMembers.value[memberIndex] = { ...teamMembers.value[memberIndex], ...updates }
      }
      
      // Update selected member if it's the same
      if (selectedTeamMember.value && selectedTeamMember.value.id === memberId) {
        selectedTeamMember.value = { ...selectedTeamMember.value, ...updates }
      }
      
      console.log('Team member updated successfully')
    }
  } catch (error) {
    console.error('Failed to update team member:', error)
    alert('Failed to update team member: ' + (error.response?.data?.message || error.message))
  }
}

// Delete member confirmation modal
const openDeleteMemberModal = (member) => {
  memberToDelete.value = member
  showDeleteMemberModal.value = true
}

const closeDeleteMemberModal = () => {
  if (!deletingMember.value) {
    showDeleteMemberModal.value = false
    memberToDelete.value = null
  }
}

const confirmRemoveTeamMember = async () => {
  if (!memberToDelete.value) return

  deletingMember.value = true
  try {
    await axios.delete(`/api/users/${memberToDelete.value.id}`)

    // Remove from local team members list
    const memberId = memberToDelete.value.id
    teamMembers.value = teamMembers.value.filter(m => m.id !== memberId)

    // Close details modal if it's the removed member
    if (selectedTeamMember.value?.id === memberId) {
      closeTeamMemberDetailsModal()
    }

    showDeleteMemberModal.value = false
    memberToDelete.value = null
    showDeleteSuccessPopup.value = true
    setTimeout(() => { showDeleteSuccessPopup.value = false }, 3000)
  } catch (error) {
    console.error('Failed to remove team member:', error)
    alert('Failed to remove team member: ' + (error.response?.data?.message || error.message))
  } finally {
    deletingMember.value = false
  }
}

// Load user timezone on mount
const loadUserTimezone = async () => {
  try {
    const userId = userStore.currentUser?.id || JSON.parse(localStorage.getItem('currentUser') || '{}').id
    if (!userId) return
    
    if (userStore.currentUser?.timezone) {
      generalSettings.value.timezone = userStore.currentUser.timezone
    } else {
      const response = await axios.get(`/api/users/${userId}`)
      if (response.data?.timezone) {
        generalSettings.value.timezone = response.data.timezone
      }
    }
  } catch (error) {
    console.error('Failed to load timezone:', error)
  }
}

// Helper function to format timezone name nicely
const formatTimezoneName = (timezone) => {
  if (!timezone) return 'Unknown'
  
  // Extract city/capital name from IANA timezone identifier
  // Examples: "Europe/Tallinn" -> "Tallinn", "America/New_York" -> "New York"
  const parts = timezone.split('/')
  if (parts.length > 1) {
    return parts[parts.length - 1].replace(/_/g, ' ')
  }
  return timezone
}

// Save timezone
const saveTimezone = async () => {
  savingTimezone.value = true
  try {
    const userId = userStore.currentUser?.id || JSON.parse(localStorage.getItem('currentUser') || '{}').id
    if (!userId) return
    
    const response = await userStore.updateUser(userId, { timezone: generalSettings.value.timezone })
    
    // If updateUser succeeds, it returns the updated user object
    if (response) {
      if (userStore.currentUser) {
        userStore.currentUser.timezone = generalSettings.value.timezone
      }
      
      // Show success popup with updated timezone
      updatedTimezone.value = generalSettings.value.timezone
      showTimezoneSuccessPopup.value = true
      
      // Auto-close popup after 3 seconds
      setTimeout(() => {
        showTimezoneSuccessPopup.value = false
      }, 3000)
    }
  } catch (error) {
    console.error('Failed to save timezone:', error)
    alert('Failed to save timezone. Please try again.')
  } finally {
    savingTimezone.value = false
  }
}

// Close timezone success popup
const closeTimezoneSuccessPopup = () => {
  showTimezoneSuccessPopup.value = false
}

// Initialize team data when component mounts
onMounted(() => {
  fetchTeamMembers()
  loadUserTimezone()
})


</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold">Settings</h1>
        <p class="text-gray-500">Dashboard / Settings</p>
      </div>
    </div>

    <!-- Settings Navigation -->
    <div class="bg-white rounded-lg shadow mb-6">
      <div class="border-b px-4">
        <nav class="flex space-x-8 overflow-x-auto">
          <button 
            v-for="tab in availableTabs"
            :key="tab"
            @click="activeTab = tab"
            class="py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap"
            :class="[
              activeTab === tab 
                ? 'border-pink-500 text-pink-500' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            {{ tabLabels[tab] || tab.charAt(0).toUpperCase() + tab.slice(1) }}
          </button>
        </nav>
      </div>
    </div>

    <!-- Settings Content -->
    <div class="bg-white rounded-lg shadow">
      <!-- General Settings -->
      <div v-if="activeTab === 'general'" class="p-6">
        <div class="space-y-6">
          <div>
            <h3 class="text-lg font-medium mb-4">General Settings</h3>
            <div class="mb-6 p-4 border rounded-lg bg-slate-50">
              <h4 class="text-sm font-semibold text-gray-800 mb-2">Combined projection model API</h4>
              <p class="text-sm text-gray-600 mb-3">
                Choose which Python worker Laravel should call for Combined model preview.
                This stays in this browser only and is sent with each preview request.
              </p>
              <ProjectionModelBackendToggle />
            </div>
            <div class="grid grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                <input 
                  v-model="generalSettings.siteName"
                  type="text"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                >
              </div>
                              <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <TimezoneSelector 
                    :selected-timezone="generalSettings.timezone"
                    @update:selected-timezone="(timezone) => generalSettings.timezone = timezone"
                  />
                  <button 
                    @click="saveTimezone"
                    :disabled="savingTimezone"
                    class="mt-3 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <i v-if="savingTimezone" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-save"></i>
                    {{ savingTimezone ? 'Saving...' : 'Save Timezone' }}
                  </button>
                </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                <select 
                  v-model="generalSettings.dateFormat"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                >
                  <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Time Format</label>
                <select 
                  v-model="generalSettings.timeFormat"
                  class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
                >
                  <option value="24h">24-hour</option>
                  <option value="12h">12-hour</option>
                </select>
              </div>
            </div>
          </div>

          <div class="pt-6 border-t">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="font-medium">Maintenance Mode</h4>
                <p class="text-sm text-gray-500">Enable maintenance mode to prevent access to the platform</p>
              </div>
              <div class="relative inline-block w-12 h-6">
                <input 
                  type="checkbox" 
                  v-model="generalSettings.maintenanceMode"
                  class="sr-only peer"
                >
                <div 
                  class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                         peer-checked:bg-pink-500"
                ></div>
                <div 
                  class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                         peer-checked:translate-x-6"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification Settings -->
      <div v-if="activeTab === 'notifications'" class="p-6">
        <h3 class="text-lg font-medium mb-4">Notification Settings</h3>
        <div class="space-y-6">
          <div v-for="(value, key) in notificationSettings" :key="key" class="flex items-center justify-between">
            <div>
              <h4 class="font-medium">{{ key.replace(/([A-Z])/g, ' $1').split(/(?=[A-Z])/).join(' ').toLowerCase() }}</h4>
              <p class="text-sm text-gray-500">Receive notifications for {{ key.toLowerCase() }}</p>
            </div>
            <div class="relative inline-block w-12 h-6">
              <input 
                type="checkbox" 
                v-model="notificationSettings[key]"
                class="sr-only peer"
              >
              <div 
                class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                       peer-checked:bg-pink-500"
              ></div>
              <div 
                class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                       peer-checked:translate-x-6"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Security Settings -->
      <div v-if="activeTab === 'security'" class="p-6 hidden">
        <h3 class="text-lg font-medium mb-4">Security Settings</h3>
        <div class="space-y-6">
          <div class="grid grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
              <input 
                v-model="securitySettings.sessionTimeout"
                type="number"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Password Expiry (days)</label>
              <input 
                v-model="securitySettings.passwordExpiry"
                type="number"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">IP Whitelist</label>
              <input 
                v-model="securitySettings.ipWhitelist"
                type="text"
                placeholder="Comma-separated IPs"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
              <input 
                v-model="securitySettings.maxLoginAttempts"
                type="number"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              >
            </div>
          </div>

          <div class="pt-6 border-t space-y-4">
            <div class="hidden flex items-center justify-between">
              <div>
                <h4 class="font-medium">Two-Factor Authentication</h4>
                <p class="text-sm text-gray-500">Require 2FA for all admin users</p>
              </div>
              <div class="relative inline-block w-12 h-6">
                <input 
                  type="checkbox" 
                  v-model="securitySettings.twoFactorAuth"
                  class="sr-only peer"
                >
                <div 
                  class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                         peer-checked:bg-pink-500"
                ></div>
                <div 
                  class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                         peer-checked:translate-x-6"
                ></div>
              </div>
            </div>

            <div class="hidden flex items-center justify-between">
              <div>
                <h4 class="font-medium">Strong Password Requirements</h4>
                <p class="text-sm text-gray-500">Enforce complex password rules</p>
              </div>
              <div class="relative inline-block w-12 h-6">
                <input 
                  type="checkbox" 
                  v-model="securitySettings.requireStrongPassword"
                  class="sr-only peer"
                >
                <div 
                  class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                         peer-checked:bg-pink-500"
                ></div>
                <div 
                  class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                         peer-checked:translate-x-6"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Password Change Section -->
      <div v-if="activeTab === 'security'" class="p-6 border-t">
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <i class="fas fa-lock text-red-600"></i>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Change Your Password</h3>
              <p class="text-sm text-gray-600">Update your account password securely</p>
            </div>
          </div>
          
          <button 
            @click="openPasswordChangeModal"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <i class="fas fa-key"></i>
            Change Password
          </button>
        </div>
        
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <i class="fas fa-exclamation-triangle text-yellow-600 mt-1"></i>
            <div>
              <h4 class="font-medium text-yellow-900">Security Notice</h4>
              <p class="text-sm text-yellow-700 mt-1">
                Changing your password will log you out of all other sessions. 
                You'll need to log in again with your new password.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Team Management -->
      <div v-if="activeTab === 'team'" class="p-6">
        <div class="space-y-6">
          <!-- Team Header -->
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <i class="fas fa-users text-blue-600"></i>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Team Management</h3>
                <p class="text-sm text-gray-600">Manage your organization team members</p>
              </div>
            </div>
            
            <button 
              @click="showAddTeamMemberModal = true"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <i class="fas fa-plus"></i>
              Add Team Member
            </button>
          </div>

          <!-- Team Stats -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div class="text-2xl font-bold text-blue-600">{{ teamMembers.length }}</div>
              <div class="text-sm text-blue-700">Team Members</div>
            </div>
            <div class="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div class="text-2xl font-bold text-green-600">
                {{ teamMembers.filter(m => m.status === 'active').length }}
              </div>
              <div class="text-sm text-green-700">Active</div>
            </div>
            <div class="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div class="text-2xl font-bold text-purple-600">
                {{ teamMembers.filter(m => m.role === 'client').length }}
              </div>
              <div class="text-sm text-purple-700">Clients</div>
            </div>
          </div>

          <!-- Search Filter -->
          <div class="flex items-center gap-4">
            <div class="relative flex-1 max-w-md">
              <input 
                v-model="teamSearchQuery"
                type="text" 
                placeholder="Search team members..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
              <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
            </div>
            <div v-if="teamSearchQuery" class="text-sm text-gray-600">
              {{ filteredTeamMembers.length }} of {{ teamMembers.length }} members
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loadingTeamMembers" class="flex justify-center items-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>

          <!-- Team Members List -->
          <div v-else-if="teamMembers.length > 0" class="space-y-3">
            <div 
              v-for="member in filteredTeamMembers" 
              :key="member.id"
              class="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              @click="viewTeamMemberDetails(member)"
            >
              <!-- Member Avatar -->
              <div class="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <span class="text-gray-600 font-medium text-lg">
                  {{ member.name ? member.name.charAt(0) : (member.email ? member.email.charAt(0) : '?') }}
                </span>
              </div>
              
              <!-- Member Info -->
              <div class="flex-1">
                <div class="font-medium text-gray-900">{{ member.name || 'Unknown User' }}</div>
                <div class="text-sm text-gray-500">{{ member.email || 'No email' }}</div>
              </div>
              
              <!-- Member Status & Role -->
              <div class="flex items-center gap-2">
                <span class="px-2 py-1 text-xs rounded-full"
                      :class="{
                        'bg-blue-100 text-blue-800': member.role === 'client',
                        'bg-green-100 text-green-800': member.role === 'trader',
                        'bg-yellow-100 text-yellow-800': member.role === 'admin',
                        'bg-gray-100 text-gray-800': !member.role
                      }">
                  {{ member.role || 'No Role' }}
                </span>
                
                <span class="px-2 py-1 text-xs rounded-full"
                      :class="{
                        'bg-green-100 text-green-800': member.status === 'active',
                        'bg-red-100 text-red-800': member.status === 'inactive',
                        'bg-yellow-100 text-yellow-800': member.status === 'pending'
                      }">
                  {{ member.status || 'Unknown' }}
                </span>
              </div>
              
              <!-- Last Active -->
              <div class="text-sm text-gray-500">
                {{ member.lastActive ? new Date(member.lastActive).toLocaleString() : 'Never' }}
              </div>
              
              <!-- Actions -->
              <div class="flex items-center gap-2">
                <button 
                  @click.stop="viewTeamMemberDetails(member)"
                  class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  title="View details"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button 
                  @click.stop="openDeleteMemberModal(member)"
                  class="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  title="Remove member"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <!-- No results message -->
            <div v-if="filteredTeamMembers.length === 0 && teamSearchQuery" 
                 class="text-center py-8 bg-gray-50 rounded-lg">
              <div class="flex flex-col items-center gap-2">
                <i class="fas fa-search text-gray-400 text-2xl"></i>
                <div>No team members found matching "{{ teamSearchQuery }}"</div>
                <button @click="teamSearchQuery = ''" class="text-blue-600 hover:text-blue-700 text-sm">
                  Clear search
                </button>
              </div>
            </div>
          </div>

          <!-- No team members -->
          <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
            <div class="text-gray-400 mb-4">
              <i class="fas fa-users text-4xl"></i>
            </div>
            <div class="text-gray-600 mb-2">No team members yet</div>
            <div class="text-sm text-gray-500 mb-4">
              Add team members to collaborate within your organization
            </div>
            <button 
              @click="showAddTeamMemberModal = true"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <i class="fas fa-plus mr-2"></i>
              Add First Team Member
            </button>
          </div>
        </div>
      </div>

      <!-- API Keys -->
      <div v-if="activeTab === 'api-keys'" class="p-6">
        <div v-if="apiKeysUser?.id" class="space-y-6">
          <div class="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div class="flex items-start gap-3 text-indigo-900">
              <i class="fas fa-info-circle mt-1"></i>
              <div class="text-sm">
                <p class="font-medium">How to use your API key</p>
                <p class="text-indigo-800 mt-1">
                  Send it as a Bearer token. The full key is shown only when you generate or rotate it.
                </p>
                <code class="block mt-2 px-3 py-2 bg-white/80 rounded border border-indigo-100 text-xs text-gray-800">Authorization: Bearer &lt;your-api-key&gt;</code>
              </div>
            </div>
          </div>
          <ApiTokenManager
            :key="apiKeysUser.id"
            :user="apiKeysUser"
            :user-store="userStore"
            self-service
          />
        </div>
        <div v-else class="text-center py-8 bg-gray-50 rounded-lg">
          <div class="text-gray-400 mb-4">
            <i class="fas fa-key text-4xl"></i>
          </div>
          <div class="text-gray-600 mb-2">Unable to load your account</div>
          <div class="text-sm text-gray-500">Sign in again to manage API keys.</div>
        </div>
      </div>

      <!-- Integration Settings -->
      <div v-if="activeTab === 'integrations'" class="p-6">
        <h3 class="text-lg font-medium mb-4">API & Integration Settings</h3>
        <div class="space-y-6">
          <div class="grid grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">API Key</label>
              <div class="flex">
                <input 
                  v-model="integrationSettings.apiKey"
                  type="text"
                  readonly
                  class="flex-1 px-4 py-2 border rounded-l-lg bg-gray-50"
                >
                <button class="px-4 py-2 bg-gray-100 border-t border-r border-b rounded-r-lg hover:bg-gray-200">
                  <i class="far fa-copy"></i>
                </button>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">API Rate Limit</label>
              <input 
                v-model="integrationSettings.apiRateLimit"
                type="number"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
              <input 
                v-model="integrationSettings.webhookUrl"
                type="url"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Callback URL</label>
              <input 
                v-model="integrationSettings.callbackUrl"
                type="url"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
              >
            </div>
          </div>

          <div class="pt-6 border-t space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="font-medium">Enable Webhooks</h4>
                <p class="text-sm text-gray-500">Send event notifications via webhooks</p>
              </div>
              <div class="relative inline-block w-12 h-6">
                <input 
                  type="checkbox" 
                  v-model="integrationSettings.enableWebhooks"
                  class="sr-only peer"
                >
                <div 
                  class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                         peer-checked:bg-pink-500"
                ></div>
                <div 
                  class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                         peer-checked:translate-x-6"
                ></div>
              </div>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <h4 class="font-medium">Enable Callbacks</h4>
                <p class="text-sm text-gray-500">Allow callback notifications</p>
              </div>
              <div class="relative inline-block w-12 h-6">
                <input 
                  type="checkbox" 
                  v-model="integrationSettings.enableCallbacks"
                  class="sr-only peer"
                >
                <div 
                  class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                         peer-checked:bg-pink-500"
                ></div>
                <div 
                  class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                         peer-checked:translate-x-6"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Appearance: interface template picker -->
      <div v-if="activeTab === 'appearance'" class="p-6">
        <UiTemplatePicker />
      </div>

      <!-- Webhook client settings preview -->
      <WebhookClientsSettings v-if="activeTab === 'webhook-clients'" />

      <!-- Boxscores Management Settings -->
      <div v-if="activeTab === 'boxscores-management'">
        <BoxscoresManagementSettings />
      </div>

      <!-- Save Button -->
      <div class="p-6 bg-gray-50 border-t hidden">
        <div class="flex justify-end">
          <button 
            @click="saveSettings"
            class="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Team Member Modal -->
  <div v-if="showAddTeamMemberModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center overflow-y-auto"
       @click.self="showAddTeamMemberModal = false"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Add Team Member</h3>
          <p class="text-sm text-gray-600 mt-1">Add a new member to your organization team</p>
        </div>
        <button @click="showAddTeamMemberModal = false" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Form -->
      <div class="p-6 space-y-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Full Name <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="newTeamMemberForm.name"
            type="text" 
            placeholder="Enter full name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-300': !newTeamMemberForm.name }"
          />
        </div>

        <!-- Email -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Email Address <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="newTeamMemberForm.email"
            type="email" 
            placeholder="Enter email address"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-300': !newTeamMemberForm.email }"
          />
        </div>

        <!-- Role -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Role
          </label>
          <select 
            v-model="newTeamMemberForm.role"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="client">Client</option>
            <option value="trader">Trader</option>
          </select>
        </div>

        <!-- Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="newTeamMemberForm.password"
            type="password" 
            placeholder="Enter password (min 8 characters)"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-300': !newTeamMemberForm.password }"
          />
          <p class="text-xs text-gray-500 mt-1">
            Must be at least 8 characters long
          </p>
        </div>

        <!-- Confirm Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="newTeamMemberForm.password_confirmation"
            type="password" 
            placeholder="Confirm password"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :class="{ 'border-red-300': !newTeamMemberForm.password_confirmation }"
          />
        </div>

        <!-- Organization Info -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-start gap-2 text-blue-800">
            <i class="fas fa-info-circle mt-1"></i>
            <div>
              <div class="font-medium text-sm">Organization:</div>
              <div class="text-xs text-blue-700 mt-1">
                {{ currentUser?.organization || currentUser?.organisation || 'Your Organization' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="showAddTeamMemberModal = false"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          :disabled="addingTeamMember"
        >
          Cancel
        </button>
        <button 
          @click="addTeamMember"
          :disabled="addingTeamMember || !newTeamMemberForm.name || !newTeamMemberForm.email || !newTeamMemberForm.password"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <div v-if="addingTeamMember" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          <i v-else class="fas fa-plus"></i>
          {{ addingTeamMember ? 'Adding...' : 'Add Member' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Team Member Details Modal -->
  <div v-if="showTeamMemberDetailsModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[130] flex items-center justify-center overflow-y-auto"
       @click.self="closeTeamMemberDetailsModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div class="flex items-center gap-4">
          <div class="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
            <span class="text-gray-600 font-medium text-lg">
              {{ selectedTeamMember?.name ? selectedTeamMember?.name.charAt(0) : (selectedTeamMember?.email ? selectedTeamMember?.email.charAt(0) : '?') }}
            </span>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">{{ selectedTeamMember?.name || 'Unknown User' }}</h3>
            <p class="text-sm text-gray-500">{{ selectedTeamMember?.email || 'No email' }}</p>
          </div>
        </div>
        <button @click="closeTeamMemberDetailsModal" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 space-y-6">
        <!-- Member Info -->
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="font-medium text-gray-700">Role:</span>
            <div class="mt-1">
              <span class="px-2 py-1 text-xs rounded-full"
                    :class="{
                      'bg-blue-100 text-blue-800': selectedTeamMember?.role === 'client',
                      'bg-green-100 text-green-800': selectedTeamMember?.role === 'trader',
                      'bg-yellow-100 text-yellow-800': selectedTeamMember?.role === 'admin',
                      'bg-gray-100 text-gray-800': !selectedTeamMember?.role
                    }">
                {{ selectedTeamMember?.role || 'No Role' }}
              </span>
            </div>
          </div>
          <div>
            <span class="font-medium text-gray-700">Status:</span>
            <div class="mt-1">
              <span class="px-2 py-1 text-xs rounded-full"
                    :class="{
                      'bg-green-100 text-green-800': selectedTeamMember?.status === 'active',
                      'bg-red-100 text-red-800': selectedTeamMember?.status === 'inactive',
                      'bg-yellow-100 text-yellow-800': selectedTeamMember?.status === 'pending'
                    }">
                {{ selectedTeamMember?.status || 'Unknown' }}
              </span>
            </div>
          </div>
          <div>
            <span class="font-medium text-gray-700">Organization:</span>
            <div class="text-gray-900">{{ selectedTeamMember?.organization || 'No Organization' }}</div>
          </div>
          <div>
            <span class="font-medium text-gray-700">Last Active:</span>
            <div class="text-gray-900">
              {{ selectedTeamMember?.lastActive ? new Date(selectedTeamMember?.lastActive).toLocaleString() : 'Never' }}
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-3 pt-4 border-t">
          <button 
            @click="updateTeamMember(selectedTeamMember.id, { status: selectedTeamMember.status === 'active' ? 'inactive' : 'active' })"
            class="px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2"
            :class="selectedTeamMember?.status === 'active' 
              ? 'bg-red-600 text-white hover:bg-red-700' 
              : 'bg-green-600 text-white hover:bg-green-700'"
          >
            <i :class="selectedTeamMember?.status === 'active' ? 'fas fa-ban' : 'fas fa-check'"></i>
            {{ selectedTeamMember?.status === 'active' ? 'Deactivate' : 'Activate' }}
          </button>
          
          <button 
            @click="openDeleteMemberModal(selectedTeamMember)"
            class="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <i class="fas fa-trash"></i>
            Remove Member
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closeTeamMemberDetailsModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Team Member Confirmation Modal -->
  <Transition
    enter-active-class="ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="showDeleteMemberModal"
         class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[135] flex items-center justify-center overflow-y-auto p-4"
         @click.self="closeDeleteMemberModal"
    >
      <Transition
        enter-active-class="ease-out duration-200"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="ease-in duration-150"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div v-if="showDeleteMemberModal" class="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
          <div class="p-6">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <i class="fas fa-user-minus text-red-600 text-xl"></i>
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-lg font-semibold text-gray-900">Remove team member?</h3>
                <p class="mt-2 text-sm text-gray-600">
                  Are you sure you want to remove
                  <span class="font-medium text-gray-900">{{ memberToDelete?.name || memberToDelete?.email || 'this person' }}</span>
                  from your team? This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 bg-gray-50 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              @click="closeDeleteMemberModal"
              :disabled="deletingMember"
              class="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              @click="confirmRemoveTeamMember"
              :disabled="deletingMember"
              class="px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
            >
              <i v-if="deletingMember" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-trash"></i>
              {{ deletingMember ? 'Removing...' : 'Yes, remove member' }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>

  <!-- Delete Success Popup -->
  <Transition
    enter-active-class="ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="showDeleteSuccessPopup"
         class="fixed top-4 right-4 z-[140] max-w-sm animate-in fade-in slide-in-from-right-4"
    >
      <div class="bg-white rounded-xl shadow-lg border border-gray-200 p-4 flex items-center gap-3">
        <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-check text-green-600"></i>
        </div>
        <div>
          <p class="font-medium text-gray-900">Team member removed</p>
          <p class="text-sm text-gray-500">The member has been successfully removed from your team.</p>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Password Change Modal -->
  <div v-if="showPasswordChangeModal" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] flex items-center justify-center overflow-y-auto"
       @click.self="closePasswordChangeModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-8">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b">
        <div>
          <h3 class="text-lg font-semibold text-gray-900">Change Your Password</h3>
          <p class="text-sm text-gray-600 mt-1">
            Update your account password securely
          </p>
        </div>
        <button @click="closePasswordChangeModal" 
                class="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Form -->
      <div class="p-6 space-y-4">
        <!-- Success Message -->
        <div v-if="passwordChangeSuccess" 
             class="bg-green-50 border border-green-200 rounded-lg p-4">
          <div class="flex items-center gap-2 text-green-800">
            <i class="fas fa-check-circle"></i>
            <span class="font-medium">Password updated successfully!</span>
          </div>
          <p class="text-sm text-green-700 mt-1">
            You will be logged out and need to log in again with your new password.
          </p>
        </div>

        <!-- Error Message -->
        <div v-if="passwordChangeError" 
             class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center gap-2 text-red-800">
            <i class="fas fa-exclamation-circle"></i>
            <span class="font-medium">Error</span>
          </div>
          <p class="text-sm text-red-700 mt-1">{{ passwordChangeError }}</p>
        </div>

        <!-- Current Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Current Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="passwordChangeForm.currentPassword"
            type="password" 
            placeholder="Enter your current password"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :disabled="changingPassword"
          />
          <p class="text-xs text-gray-500 mt-1">
            Enter your current password to verify your identity
          </p>
        </div>

        <!-- New Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            New Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="passwordChangeForm.newPassword"
            type="password" 
            placeholder="Enter new password (min 8 characters)"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :disabled="changingPassword"
          />
          <p class="text-xs text-gray-500 mt-1">
            Must be at least 8 characters long
          </p>
        </div>

        <!-- Confirm New Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password <span class="text-red-500">*</span>
          </label>
          <input 
            v-model="passwordChangeForm.confirmPassword"
            type="password" 
            placeholder="Confirm new password"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            :disabled="changingPassword"
            @keyup.enter="updateUserPassword"
          />
          <p class="text-xs text-gray-500 mt-1">
            Re-enter the new password to confirm
          </p>
        </div>

        <!-- Password Requirements -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-start gap-2 text-blue-800">
            <i class="fas fa-info-circle mt-1"></i>
            <div>
              <div class="font-medium text-sm">Password Requirements:</div>
              <ul class="text-xs text-blue-700 mt-1 space-y-1">
                <li>• Minimum 8 characters long</li>
                <li>• Should be different from current password</li>
                <li>• Consider using a mix of letters, numbers, and symbols</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Security Notice -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div class="flex items-start gap-2 text-yellow-800">
            <i class="fas fa-exclamation-triangle mt-1"></i>
            <div>
              <div class="font-medium text-sm">Security Notice:</div>
              <p class="text-xs text-yellow-700 mt-1">
                After changing your password, you will be logged out of all sessions and need to log in again with your new password.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closePasswordChangeModal"
          class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          :disabled="changingPassword"
        >
          Cancel
        </button>
        <button 
          @click="updateUserPassword"
          :disabled="changingPassword || !passwordChangeForm.currentPassword || !passwordChangeForm.newPassword || !passwordChangeForm.confirmPassword"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <div v-if="changingPassword" class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
          <i v-else class="fas fa-key"></i>
          {{ changingPassword ? 'Updating...' : 'Update Password' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Timezone Success Popup -->
  <div v-if="showTimezoneSuccessPopup" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[140] flex items-center justify-center overflow-y-auto"
       @click.self="closeTimezoneSuccessPopup"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md m-8 animate-in fade-in slide-in-from-bottom-4">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-xl">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
            <i class="fas fa-check text-white text-xl"></i>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Timezone Updated!</h3>
            <p class="text-sm text-gray-600">Your timezone has been successfully updated</p>
          </div>
        </div>
        <button @click="closeTimezoneSuccessPopup" 
                class="p-2 hover:bg-white/50 rounded-lg transition-colors">
          <i class="fas fa-times text-gray-600"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-6">
        <div class="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
          <div class="flex items-center justify-center mb-4">
            <div class="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center">
              <i class="fas fa-globe text-white text-2xl"></i>
            </div>
          </div>
          
          <div class="text-center">
            <div class="text-2xl font-bold text-gray-900 mb-2">
              {{ formatTimezoneName(updatedTimezone) }}
            </div>
            <div class="text-sm text-gray-600 font-mono mb-4">
              {{ updatedTimezone }}
            </div>
            
            <div class="flex items-center justify-center gap-2 text-green-600">
              <i class="fas fa-check-circle"></i>
              <span class="font-medium">Successfully updated</span>
            </div>
          </div>
        </div>
        
        <div class="mt-4 text-center text-sm text-gray-500">
          This popup will close automatically in a few seconds
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
        <button 
          @click="closeTimezoneSuccessPopup"
          class="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2"
        >
          <i class="fas fa-check"></i>
          Got it
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Toggle button transitions */
.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}
</style>
